// Dispatch orchestration for SiteCaptureScreen asset-row edits.
// Extracted from site-capture-screen.tsx so that file stays under the
// max-lines policy, mirroring room-capture-removal.ts. The screen owns
// the React state (override map + in-flight-create ref); this module
// owns the create-vs-update branching, the duplicate-create guard, and
// the echo reconciliation.
//
// Rows are addressed by `assetTypeId`, NOT `id`: a template-only row
// synthesised by useSiteItems carries id '' and several would otherwise
// collapse onto one React key / one override entry. Each asset type
// appears at most once in a site session (persisted OR template-only,
// never both — useSiteItems dedupes), so assetTypeId is a stable unique
// key for both states.

import type { Dispatch, SetStateAction } from 'react'

import type { CaptureItemRow } from '@citycare/shared'

import type { CaptureClient } from '../client/capture-client.types'
import type { AssetRowPatch } from '../components/asset-row'

import {
  applyOptimisticPatch,
  extractEchoedItem,
  reconcileEchoOverride,
} from './site-capture-reconcile'
import {
  mergeAuthoritativeWithPending,
  type PendingCreate,
} from './room-capture-reconcile'

type OverrideSetter = Dispatch<SetStateAction<ReadonlyMap<string, CaptureItemRow>>>

export interface SiteItemUpdateDeps {
  captureClient: Pick<CaptureClient, 'dispatch'>
  // The session whose site items are being captured. Empty string while
  // the screen has no active session (the updater no-ops then).
  sessionId: string
  // The rows currently on screen (post-override), used to resolve the
  // edited row by assetTypeId.
  items: readonly CaptureItemRow[]
  setOverrides: OverrideSetter
  // assetTypeId → latest optimistic values for an in-flight createItem.
  // A stable Map (the screen holds it in a ref and reads `.current` at
  // event time); this module mutates it directly.
  pendingCreates: Map<string, PendingCreate>
}

// Persisted-row edit: PUT /capture/items/:id via the coalescing client.
// The echo reconciles back into the override (preserving display fields
// the local JOIN may have dropped after a WiFi drop).
function dispatchUpdate(
  deps: SiteItemUpdateDeps,
  assetTypeId: string,
  existing: CaptureItemRow,
  nextRow: CaptureItemRow,
): void {
  const { captureClient, setOverrides } = deps
  void captureClient
    .dispatch({
      kind: 'updateItem',
      itemId: existing.id,
      quantity: nextRow.quantity,
      isZeroConfirmed: nextRow.isZeroConfirmed,
      notes: existing.notes,
      syncVersion: existing.syncVersion,
    })
    .then((result) => {
      const serverItem = extractEchoedItem(result)
      if (serverItem === null) return
      setOverrides((prev) =>
        reconcileEchoOverride(prev, assetTypeId, serverItem, nextRow),
      )
    })
    .catch(() => {
      // Permanent failures emit mutation.failed telemetry via the
      // client. The optimistic override remains (LWW — last write wins).
    })
}

// Template-only-row first write: POST /capture/items (createItem). The
// counter auto-repeats on press-and-hold, so a single hold fires many
// taps before the first create echoes. dispatchCreateItem mints a fresh
// UUID per call, so dispatching once per tap would INSERT duplicate rows
// (duplicate counts — the cardinal sin). Guard: only the FIRST tap
// dispatches; later taps update the in-flight pending payload. When the
// create echoes (first-write-wins may persist an earlier quantity), a
// catch-up updateItem brings the server to the user's latest value so no
// tap is lost. Mirrors RoomCaptureScreen's pendingCreates flow.
function dispatchCreate(
  deps: SiteItemUpdateDeps,
  assetTypeId: string,
  existing: CaptureItemRow,
  nextRow: CaptureItemRow,
): void {
  const { captureClient, sessionId, setOverrides, pendingCreates } = deps
  const alreadyInFlight = pendingCreates.has(assetTypeId)
  pendingCreates.set(assetTypeId, {
    quantity: nextRow.quantity,
    isZeroConfirmed: nextRow.isZeroConfirmed,
    notes: existing.notes,
  })
  if (alreadyInFlight) return

  void captureClient
    .dispatch({
      kind: 'createItem',
      sessionId,
      spaceId: null,
      assetTypeId,
      quantity: nextRow.quantity,
      isZeroConfirmed: nextRow.isZeroConfirmed,
      notes: existing.notes,
    })
    .then((result) => {
      const pending = pendingCreates.get(assetTypeId)
      pendingCreates.delete(assetTypeId)
      const serverItem = extractEchoedItem(result)
      if (serverItem === null) return
      const { row: merged, serverIsBehind } = mergeAuthoritativeWithPending(
        serverItem,
        pending,
      )
      setOverrides((prev) =>
        reconcileEchoOverride(prev, assetTypeId, merged, nextRow),
      )
      if (serverIsBehind) {
        void captureClient
          .dispatch({
            kind: 'updateItem',
            itemId: serverItem.id,
            quantity: merged.quantity,
            isZeroConfirmed: merged.isZeroConfirmed,
            notes: merged.notes,
            syncVersion: serverItem.syncVersion,
          })
          .catch(() => {
            // Best-effort catch-up; the override already shows the
            // user's latest value and the next tap retries via update.
          })
      }
    })
    .catch(() => {
      pendingCreates.delete(assetTypeId)
    })
}

// Build the per-row update handler the screen binds to each AssetRow.
// Returns a function keyed by assetTypeId: it records an optimistic
// override immediately, then routes to createItem (synthetic / template-
// only row) or updateItem (persisted row).
export function makeSiteItemUpdate(
  deps: SiteItemUpdateDeps,
): (assetTypeId: string, patch: AssetRowPatch) => void {
  return (assetTypeId, patch) => {
    if (deps.sessionId === '') return
    const existing = deps.items.find((it) => it.assetTypeId === assetTypeId)
    if (existing === undefined) return

    const nextRow = applyOptimisticPatch(existing, patch)
    deps.setOverrides((prev) => {
      const next = new Map(prev)
      next.set(assetTypeId, nextRow)
      return next
    })

    const isSynthetic = existing.id === '' || existing.isTemplateOnly
    if (isSynthetic) {
      dispatchCreate(deps, assetTypeId, existing, nextRow)
    } else {
      dispatchUpdate(deps, assetTypeId, existing, nextRow)
    }
  }
}
